import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useEOSAuth } from "../../contexts/EOSAuthContext";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://engineeringofstockbackend.onrender.com";

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

export type TradeSummary = {
  total_trades?: number;
  total_pnl?: number;
  success_rate?: number;
  avg_pnl?: number;
};

export type TradeRange = {
  startDate: Date | null;
  endDate: Date | null;
};

export function useTradeDashboard() {
  const { token, user } = useEOSAuth();
  const navigate = useNavigate();
  const [trades, setTrades] = useState<any[]>([]);
  const [summary, setSummary] = useState<TradeSummary>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<TradeRange>({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
    endDate: new Date(),
  });
  const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false);

  useEffect(() => {
    if (token) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common["Authorization"];
    }
  }, [token]);

  const fetchTrades = async (start: Date | null, end: Date | null) => {
    try {
      setLoading(true);
      setError(null);

      const startStr = start ? start.toISOString().split("T")[0] : "";
      const endStr = end ? end.toISOString().split("T")[0] : "";

      const [tradesResponse, summaryResponse] = await Promise.all([
        api.get(`/api/trades?start=${startStr}&end=${endStr}`),
        api.get(`/api/trades/summary?start=${startStr}&end=${endStr}`),
      ]);

      const tradesData = tradesResponse.data?.data?.trades || tradesResponse.data?.trades || [];
      setTrades(Array.isArray(tradesData) ? tradesData : []);

      const summaryData = summaryResponse.data?.data || summaryResponse.data || {};
      setSummary(summaryData);
    } catch (err: any) {
      setTrades([]);
      setSummary({});

      if (!err.message?.includes("Session expired")) {
        setError("Failed to fetch trades. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrades(dateRange.startDate, dateRange.endDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  const handleDateRangeChange = (newDateRange: TradeRange) => {
    setDateRange(newDateRange);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const clearError = () => {
    setError(null);
  };

  const applyPresetLast7Days = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);
    setDateRange({ startDate: start, endDate: end });
  };

  const applyPresetLast30Days = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    setDateRange({ startDate: start, endDate: end });
  };

  const applyPresetLastMonth = () => {
    const end = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 1);
    setDateRange({ startDate: start, endDate: end });
  };

  const applyCustomRangeIfComplete = () => {
    if (dateRange.startDate && dateRange.endDate) {
      setIsCalendarModalOpen(false);
    }
  };

  return {
    user,
    navigate,
    trades,
    summary,
    loading,
    error,
    dateRange,
    setDateRange,
    isCalendarModalOpen,
    setIsCalendarModalOpen,
    fetchTrades,
    handleDateRangeChange,
    formatCurrency,
    formatPercentage,
    clearError,
    applyPresetLast7Days,
    applyPresetLast30Days,
    applyPresetLastMonth,
    applyCustomRangeIfComplete,
  };
}
